/**
 * Listado administrativo de Combos — POS-COMBOS-C1A-DOMAIN-001
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaPlus,
  FaEdit,
  FaArchive,
  FaToggleOn,
  FaToggleOff,
  FaArrowLeft,
  FaBoxes
} from 'react-icons/fa';

import combosService from '../../services/combos.service';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import SearchBar from '../../components/common/SearchBar';
import { formatCurrency } from '../../utils/format';

const ESTADO_LABEL = {
  draft: 'Borrador',
  active: 'Activo',
  inactive: 'Inactivo'
};

const Combos = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const puedeVer = hasPermission('combos', 'ver');
  const puedeCrear = hasPermission('combos', 'crear');
  const puedeEditar = hasPermission('combos', 'editar');
  const puedeEliminar = hasPermission('combos', 'eliminar');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busyId, setBusyId] = useState(null);

  const cargar = useCallback(async () => {
    if (!puedeVer) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await combosService.listar();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'No se pudieron cargar los Combos');
    } finally {
      setLoading(false);
    }
  }, [puedeVer]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const filtrados = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return items.filter((c) => {
      if (filtroEstado && c.estado !== filtroEstado) return false;
      if (!term) return true;
      const hay = `${c.nombre || ''} ${c.descripcion || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [items, searchTerm, filtroEstado]);

  const onActivar = async (combo) => {
    if (!puedeEditar) return;
    try {
      setBusyId(combo.id);
      await combosService.activar(combo.id);
      toast.success('Combo activado');
      await cargar();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0]?.message ||
        'No se pudo activar';
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const onDesactivar = async (combo) => {
    if (!puedeEditar) return;
    try {
      setBusyId(combo.id);
      await combosService.desactivar(combo.id);
      toast.success('Combo desactivado');
      await cargar();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'No se pudo desactivar');
    } finally {
      setBusyId(null);
    }
  };

  const onArchivar = async (combo) => {
    if (!puedeEliminar) return;
    if (!window.confirm(`¿Archivar el Combo "${combo.nombre}"?`)) return;
    try {
      setBusyId(combo.id);
      await combosService.archivar(combo.id);
      toast.success('Combo archivado');
      await cargar();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'No se pudo archivar');
    } finally {
      setBusyId(null);
    }
  };

  if (!puedeVer) {
    return (
      <Card>
        <p className="text-e0e3-muted">No tenés permiso para ver Combos.</p>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <FaBoxes className="text-e0e3-accent text-2xl" />
            <h1 className="text-2xl font-bold text-e0e3-foreground">Combos</h1>
          </div>
          <p className="text-e0e3-muted mt-1">
            Packs / kits administrativos · precio propio · sin tocar listas base
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button color="secondary" icon={<FaArrowLeft />} onClick={() => navigate('/centro-comercial')}>
            Centro Comercial
          </Button>
          {puedeCrear && (
            <Button color="primary" icon={<FaPlus />} onClick={() => navigate('/combos/nuevo')}>
              Nuevo Combo
            </Button>
          )}
        </div>
      </div>

      <Card>
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar Combo…"
            />
          <select
            className="input min-h-[44px]"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
          </select>
        </div>

        {filtrados.length === 0 ? (
          <p className="text-e0e3-muted py-8 text-center">No hay Combos para mostrar.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="nexo-table w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left">Nombre</th>
                  <th className="text-left">Estado</th>
                  <th className="text-right">Precio</th>
                  <th className="text-right">Costo</th>
                  <th className="text-right">Margen</th>
                  <th className="text-right">Ítems</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((combo) => (
                  <tr key={combo.id}>
                    <td>
                      <Link
                        to={`/combos/editar/${combo.id}`}
                        className="text-e0e3-accent-soft hover:underline font-medium"
                      >
                        {combo.nombre}
                      </Link>
                      {combo.descripcion ? (
                        <div className="text-xs text-e0e3-muted">{combo.descripcion}</div>
                      ) : null}
                    </td>
                    <td>{ESTADO_LABEL[combo.estado] || combo.estado}</td>
                    <td className="text-right">{formatCurrency(combo.precioVenta || 0)}</td>
                    <td className="text-right">
                      {combo.costoTotal != null ? formatCurrency(combo.costoTotal) : '—'}
                    </td>
                    <td className="text-right">
                      {combo.margenPct != null ? `${combo.margenPct}%` : '—'}
                    </td>
                    <td className="text-right">{combo.componentes?.length || 0}</td>
                    <td className="text-right">
                      <div className="inline-flex gap-2 justify-end">
                        {puedeEditar && (
                          <button
                            type="button"
                            className="text-e0e3-accent-soft"
                            title="Editar"
                            onClick={() => navigate(`/combos/editar/${combo.id}`)}
                          >
                            <FaEdit />
                          </button>
                        )}
                        {puedeEditar && combo.estado !== 'active' && (
                          <button
                            type="button"
                            className="text-e0e3-ok"
                            title="Activar"
                            disabled={busyId === combo.id}
                            onClick={() => onActivar(combo)}
                          >
                            <FaToggleOn />
                          </button>
                        )}
                        {puedeEditar && combo.estado === 'active' && (
                          <button
                            type="button"
                            className="text-e0e3-warn"
                            title="Desactivar"
                            disabled={busyId === combo.id}
                            onClick={() => onDesactivar(combo)}
                          >
                            <FaToggleOff />
                          </button>
                        )}
                        {puedeEliminar && (
                          <button
                            type="button"
                            className="text-e0e3-danger"
                            title="Archivar"
                            disabled={busyId === combo.id}
                            onClick={() => onArchivar(combo)}
                          >
                            <FaArchive />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Combos;
